import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { CourseCompletionSummary, CourseDetailedAnalytics, ModuleAnalytics } from "../../../graphql/generated/graphql";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import ModulePanel from "./ModulePanel";

interface CourseDetailProps {
  courseId: string;
  onBackToCourses: () => void;
  completionData: CourseCompletionSummary[];
  detailedCourse: CourseDetailedAnalytics;
  loading: boolean;
}

const hasEngagement = (item: ModuleAnalytics): boolean => {
  return (
    (item.success_points && item.success_points > 0) ||
    (item.confusion_points && item.confusion_points > 0) ||
    (item.questions_asked && item.questions_asked > 0) ||
    (item.active_interaction_count && item.active_interaction_count > 0) ||
    (item.distractions && item.distractions > 0) ||
    (item.average_time_taken && item.average_time_taken > 0)
  );
};

const sortByEngagement = (items: ModuleAnalytics[]): ModuleAnalytics[] => {
  return [...items].sort((a, b) => {
    const aActive = hasEngagement(a);
    const bActive = hasEngagement(b);
    if (aActive === bActive) return 0;
    return aActive ? -1 : 1;
  });
};

const getSentimentColor = (sentiment: string): string => {
  switch (sentiment?.toLowerCase()) {
    case "positive":
      return "#10B981"; // green
    case "negative":
      return "#EF4444"; // red
    case "neutral":
    default:
      return "#6B7280"; // gray
  }
};

const CourseDetail: React.FC<CourseDetailProps> = ({
  courseId,
  onBackToCourses,
  completionData,
  detailedCourse,
  loading: externalLoading,
}) => {
  const completion = completionData.find((c) => c.id === courseId);

  const renderModules = (modules: ModuleAnalytics[]) => {
    if (!modules || modules.length === 0) {
      return <CustomText text="No modules found." size="s" useTranslationText={false} />;
    }
    const sorted = sortByEngagement(modules);
    return (
      <View>
        {sorted.map((module, index) => (
          <ModulePanel
            key={module.id || index}
            module={module}
            index={index}
            inactive={!hasEngagement(module)}
            loading={externalLoading}
          />
        ))}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container}>
      <TouchableOpacity style={styles.backButton} onPress={onBackToCourses}>
        <CustomText text="← Back to Courses" size="m" useTranslationText={false} />
      </TouchableOpacity>

      <CustomSpacing type="vertical" size="m" />

      <View style={styles.header}>
        <CustomText text={detailedCourse?.label} size="xl" weight="bold" useTranslationText={false} />
        {detailedCourse?.sentiment && (
          <View
            style={[
              styles.sentimentBadge,
              {
                backgroundColor: getSentimentColor(detailedCourse?.sentiment) + "20",
                borderColor: getSentimentColor(detailedCourse?.sentiment),
              },
            ]}>
            <CustomText
              text={detailedCourse?.sentiment}
              size="s"
              style={{ color: getSentimentColor(detailedCourse?.sentiment) }}
              useTranslationText={false}
            />
          </View>
        )}
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Key Metrics */}
      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <CustomText
            text="Success Points"
            size="s"
            weight="bold"
            style={{ color: "#10B981" }}
            useTranslationText={false}
          />
          <CustomText text={`${detailedCourse?.success_points}`} size="xl" weight="bold" useTranslationText={false} />
        </View>

        <View style={styles.statCard}>
          <CustomText
            text="Confusion Points"
            size="s"
            weight="bold"
            style={{ color: "#EF4444" }}
            useTranslationText={false}
          />
          <CustomText text={`${detailedCourse?.confusion_points}`} size="xl" weight="bold" useTranslationText={false} />
        </View>

        <View style={styles.statCard}>
          <CustomText
            text="Questions Asked"
            size="s"
            weight="bold"
            style={{ color: "#3B82F6" }}
            useTranslationText={false}
          />
          <CustomText text={`${detailedCourse?.questions_asked}`} size="xl" weight="bold" useTranslationText={false} />
        </View>

        <View style={styles.statCard}>
          <CustomText
            text="Enrollment"
            size="s"
            weight="bold"
            style={{ color: "#8B5CF6" }}
            useTranslationText={false}
          />
          <CustomText text={`${detailedCourse?.enrollment}`} size="xl" weight="bold" useTranslationText={false} />
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Completion Statistics */}
      {completion && (
        <View style={styles.completionCard}>
          <CustomText text="Completion Statistics" size="l" weight="bold" useTranslationText={false} />
          <CustomSpacing type="vertical" size="m" />

          <View style={styles.completionRow}>
            <CustomText text="Completed:" size="m" useTranslationText={false} />
            <CustomText
              text={`${completion.completionStats.completed}`}
              size="m"
              weight="bold"
              useTranslationText={false}
            />
          </View>
          <View style={styles.completionRow}>
            <CustomText text="In Progress:" size="m" useTranslationText={false} />
            <CustomText
              text={`${completion.completionStats.inProgress}`}
              size="m"
              weight="bold"
              useTranslationText={false}
            />
          </View>
          <View style={styles.completionRow}>
            <CustomText text="Not Started:" size="m" useTranslationText={false} />
            <CustomText
              text={`${completion.completionStats.notStarted}`}
              size="m"
              weight="bold"
              useTranslationText={false}
            />
          </View>
        </View>
      )}

      <CustomSpacing type="vertical" size="l" />

      {/* Course Modules Drill-down */}
      <View style={styles.modulesSection}>
        <CustomText text="Course Modules" size="l" weight="bold" useTranslationText={false} />
        <CustomSpacing type="vertical" size="m" />
        {detailedCourse?.details?.modules ? (
          renderModules(detailedCourse?.details.modules)
        ) : (
          <CustomText text="No module data available." size="s" useTranslationText={false} />
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    padding: 16,
  },
  backButton: {
    padding: 8,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    alignSelf: "flex-start",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  sentimentBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    marginLeft: 8,
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
  statCard: {
    flex: 1,
    minWidth: 120,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  completionCard: {
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  completionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  modulesSection: {
    marginBottom: 24,
  },
});

export default CourseDetail;
